import { useCallback, useMemo, type Dispatch, type SetStateAction } from "react";
import type {
  ProductDetailResponse,
} from "@cabal/shared/domain/session/product/types.js";
import type {
  VersionDetailResponse,
} from "@cabal/shared/domain/session/version/types.js";

interface UseSessionDetailCacheInput {
  setProductDetailsById: Dispatch<
    SetStateAction<Record<string, ProductDetailResponse>>
  >;
  setVersionDetailsById: Dispatch<
    SetStateAction<Record<string, VersionDetailResponse>>
  >;
}

interface SessionDetailCacheActions {
  cacheProductDetail: (input: { detail: ProductDetailResponse }) => void;
  cacheVersionDetail: (input: { detail: VersionDetailResponse }) => void;
}

export function useSessionDetailCache(
  input: UseSessionDetailCacheInput,
): SessionDetailCacheActions {
  const { setProductDetailsById, setVersionDetailsById } = input;

  const cacheProductDetail = useCallback(
    (cacheInput: { detail: ProductDetailResponse }): void => {
      setProductDetailsById((prev) => {
        if (prev[cacheInput.detail.id] === cacheInput.detail) {
          return prev;
        }

        return {
          ...prev,
          [cacheInput.detail.id]: cacheInput.detail,
        };
      });
    },
    [setProductDetailsById],
  );

  const cacheVersionDetail = useCallback(
    (cacheInput: { detail: VersionDetailResponse }): void => {
      setVersionDetailsById((prev) => {
        if (prev[cacheInput.detail.id] === cacheInput.detail) {
          return prev;
        }

        return {
          ...prev,
          [cacheInput.detail.id]: cacheInput.detail,
        };
      });
    },
    [setVersionDetailsById],
  );

  return useMemo(
    () => ({
      cacheProductDetail,
      cacheVersionDetail,
    }),
    [cacheProductDetail, cacheVersionDetail],
  );
}
